import { cssProps } from '@oracle/oraclejet-cssprops';
import keys from 'ramda/src/keys';
import { Size, sizeToCSS } from '../../../utils/UNSAFE_size';
import { coerceArray } from '../../../utils/UNSAFE_arrayUtils';

// boxalignment are styles that help with alignment for Grid/Flex styles, like justifyContent,
// align, and gap.

const alignStyles = {
  baseline: cssProps`${{
    alignItems: 'baseline'
  }}`,

  center: cssProps`${{
    alignItems: 'center'
  }}`,

  end: cssProps`${{
    alignItems: 'flex-end'
  }}`,

  start: cssProps`${{
    alignItems: 'flex-start'
  }}`,

  inherit: cssProps`${{
    alignItems: 'inherit'
  }}`,

  initial: cssProps`${{
    alignItems: 'initial'
  }}`,

  stretch: cssProps`${{
    alignItems: 'stretch'
  }}`
};

const justifyStyles = {
  center: cssProps`${{
    justifyContent: 'center'
  }}`,

  end: cssProps`${{
    justifyContent: 'flex-end'
  }}`,

  start: cssProps`${{
    justifyContent: 'flex-start'
  }}`,

  inherit: cssProps`${{
    justifyContent: 'inherit'
  }}`,

  initial: cssProps`${{
    justifyContent: 'initial'
  }}`,

  around: cssProps`${{
    justifyContent: 'space-around'
  }}`,

  between: cssProps`${{
    justifyContent: 'space-between'
  }}`,

  evenly: cssProps`${{
    justifyContent: 'space-evenly'
  }}`
};

// See https://stackoverflow.com/questions/55377365/what-does-keyof-typeof-mean-in-typescript
// typeof operator gives you the type of an object
// then keyof operator gives you the literal type union
// derive the types of each property from the implementation
export type BoxAlignmentProps = {
  align?: keyof typeof alignStyles;
  gap?: Size | [Size] | [Size, Size];
  justify?: keyof typeof justifyStyles;
};

const boxAlignmentInterpolations = {
  align: ({ align }: Pick<BoxAlignmentProps, 'align'>) =>
    align === undefined
      ? {}
      : {
          class: alignStyles[align]
        },
  justify: ({ justify }: Pick<BoxAlignmentProps, 'justify'>) =>
    justify === undefined
      ? {}
      : {
          class: justifyStyles[justify]
        },
  // See https://developer.mozilla.org/en-US/docs/Web/CSS/gap
  gap: ({ gap }: Pick<BoxAlignmentProps, 'gap'>) => {
    if (gap === undefined) {
      return {};
    } else {
      const [rowSize, columnSize = rowSize] = coerceArray(gap) as [Size] | [Size, Size];
      return { gap: `${sizeToCSS(rowSize)} ${sizeToCSS(columnSize)}` };
    }
  }
};

// These consts are exported so they can be used in stories to show the allowed options
// e.g., See Flex.stories.tsx:   align: {control: 'select',options: aligns}
const aligns = keys(alignStyles);
const justifies = keys(justifyStyles);

export { aligns, justifies, boxAlignmentInterpolations };
